/**
 * Represents a structured API error response
 */
export interface ApiErrorResponse {
  error_code: string;
  message: string;
  description?: string;
  detail?: Record<string, unknown>;
}

/**
 * Custom error class for API errors with structured data
 */
export class ApiError extends Error {
  public readonly errorCode: string;
  public readonly description?: string;
  public readonly detail?: Record<string, unknown>;
  public readonly statusCode: number;

  constructor(
    message: string,
    errorCode: string,
    statusCode: number,
    description?: string,
    detail?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.description = description;
    this.detail = detail;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Gets the most user-friendly error message available
   * Prioritizes: description > message > generic fallback
   */
  public getUserMessage(): string {
    return this.description || this.message || "An unexpected error occurred";
  }

  /**
   * Creates an ApiError from a structured API error response
   */
  public static fromResponse(
    response: ApiErrorResponse,
    statusCode: number
  ): ApiError {
    return new ApiError(
      response.message,
      response.error_code,
      statusCode,
      response.description,
      response.detail
    );
  }
}
